import { useState, useEffect } from "react"
import { Link, useNavigate } from "react-router-dom"
import { lumi } from "@/lib/lumi"
import { useAuth } from "@/hooks/useAuth"
import {Shield, Mail, Lock, User, ArrowRight, Eye, EyeOff, Loader2, Send, Chrome} from 'lucide-react'

type AuthMode = "signin" | "signup" | "reset"

export default function AuthPage() {
  const { isAuthenticated, isAdmin } = useAuth()
  const navigate = useNavigate()
  const [mode, setMode] = useState<AuthMode>("signin")
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [userName, setUserName] = useState("")
  const [otp, setOtp] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const [otpSent, setOtpSent] = useState(false)

  useEffect(() => {
    if (isAuthenticated) {
      navigate(isAdmin ? "/admin" : "/espace-client")
    }
  }, [isAuthenticated, isAdmin, navigate])

  useEffect(() => {
    if (countdown > 0) {
      const t = setTimeout(() => setCountdown(countdown - 1), 1000)
      return () => clearTimeout(t)
    }
  }, [countdown])

  const sendOtp = async () => {
    if (!email) return setError("Veuillez entrer votre email")
    setError("")
    try {
      await lumi.auth.sendOtp({ email, type: mode === "reset" ? "resetPassword" : "register" })
      setCountdown(60)
      setOtpSent(true)
    } catch (e: any) {
      setError(e?.message || "Erreur lors de l'envoi du code")
    }
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")
    setLoading(true)
    try {
      if (mode === "signin") {
        await lumi.auth.signInWithPassword({ email, password })
      } else if (mode === "signup") {
        await lumi.auth.signUpWithPassword({ email, password, otp, userName })
      } else if (mode === "reset") {
        await lumi.auth.resetPassword({ email, newPassword: password, otp })
      }
    } catch (e: any) {
      setError(e?.message || "Une erreur est survenue")
    }
    setLoading(false)
  }

  const signInWithGoogle = async () => {
    setError("")
    setLoading(true)
    try {
      await lumi.auth.signInWithOAuth({ provider: 'google' })
    } catch (e: any) {
      setError(e?.message || "Erreur lors de la connexion avec Google")
      setLoading(false)
    }
  }

  const switchMode = (newMode: AuthMode) => {
    setMode(newMode)
    setError("")
    setOtp("")
    setOtpSent(false)
    setCountdown(0)
  }

  const titles: Record<AuthMode, string> = {
    signin: "Connexion",
    signup: "Créer un compte",
    reset: "Réinitialiser le mot de passe",
  }

  return (
    <div className="min-h-screen pt-24 lg:pt-32 pb-20 flex items-start justify-center">
      <div className="w-full max-w-md mx-auto section-padding">
        {/* Logo */}
        <div className="text-center mb-8 animate-fade-in-up">
          <Link to="/" className="inline-flex items-center gap-2.5 mb-6">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-emerald-500 flex items-center justify-center">
              <Shield className="w-6 h-6 text-slate-950" strokeWidth={2.5} />
            </div>
            <span className="text-xl font-bold tracking-tight text-white">
              LJJ <span className="text-cyan-400">IT</span>
            </span>
          </Link>
          <h1 className="text-2xl font-bold text-white">{titles[mode]}</h1>
          <p className="text-sm text-slate-400 mt-1">
            {mode === "signin" && "Accédez à votre espace client"}
            {mode === "signup" && "Rejoignez LJJ IT pour gérer vos tickets"}
            {mode === "reset" && "Entrez votre email pour recevoir un code"}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={submit} className="card !p-8 space-y-5 animate-fade-in-up" style={{ animationDelay: "0.1s" }}>
          {error && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
              {error}
            </div>
          )}

          {mode === "signup" && (
            <div>
              <label className="label-field">Nom complet</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-slate-500" />
                <input
                  type="text"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  placeholder="Jean Dupont"
                  className="input-field !pl-10"
                  required
                />
              </div>
            </div>
          )}

          <div>
            <label className="label-field">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-slate-500" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="vous@entreprise.com"
                className="input-field !pl-10"
                required
              />
            </div>
          </div>

          <div>
            <label className="label-field">
              {mode === "reset" ? "Nouveau mot de passe" : "Mot de passe"}
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-slate-500" />
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="input-field !pl-10 !pr-10"
                required
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
              >
                {showPassword ? <EyeOff className="w-4.5 h-4.5" /> : <Eye className="w-4.5 h-4.5" />}
              </button>
            </div>
          </div>

          {(mode === "signup" || mode === "reset") && (
            <div>
              <label className="label-field">Code de vérification (OTP)</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  placeholder="123456"
                  className="input-field flex-1"
                  required
                />
                <button
                  type="button"
                  onClick={sendOtp}
                  disabled={countdown > 0}
                  className="shrink-0 px-4 py-3 rounded-lg bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-sm font-medium hover:bg-cyan-500/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                >
                  <Send className="w-3.5 h-3.5" />
                  {countdown > 0 ? `${countdown}s` : "Envoyer"}
                </button>
              </div>
              {otpSent && (
                <p className="text-xs text-emerald-400 mt-1.5">
                  Code envoyé ! Vérifiez votre boîte email.
                </p>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || ((mode === "signup" || mode === "reset") && !otpSent)}
            className="btn-primary w-full justify-center !py-3.5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                {mode === "signin" && "Se connecter"}
                {mode === "signup" && "Créer mon compte"}
                {mode === "reset" && "Réinitialiser"}
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>

          {mode === "signin" && (
            <>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-700"></div>
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-slate-800 px-2 text-slate-500">Ou continuer avec</span>
                </div>
              </div>

              <button
                type="button"
                onClick={signInWithGoogle}
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 px-4 py-3.5 bg-white hover:bg-gray-50 text-gray-700 rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow"
              >
                <Chrome className="w-5 h-5" />
                Se connecter avec Google
              </button>
            </>
          )}

          {/* Mode switchers */}
          <div className="text-center text-sm space-y-2 pt-2">
            {mode === "signin" && (
              <>
                <p className="text-slate-400">
                  Pas encore de compte ?{" "}
                  <button type="button" onClick={() => switchMode("signup")} className="text-cyan-400 hover:underline font-medium">
                    Créer un compte
                  </button>
                </p>
                <p>
                  <button type="button" onClick={() => switchMode("reset")} className="text-slate-500 hover:text-slate-300 text-xs">
                    Mot de passe oublié ?
                  </button>
                </p>
              </>
            )}
            {mode === "signup" && (
              <p className="text-slate-400">
                Déjà un compte ?{" "}
                <button type="button" onClick={() => switchMode("signin")} className="text-cyan-400 hover:underline font-medium">
                  Se connecter
                </button>
              </p>
            )}
            {mode === "reset" && (
              <p className="text-slate-400">
                <button type="button" onClick={() => switchMode("signin")} className="text-cyan-400 hover:underline font-medium">
                  Retour à la connexion
                </button>
              </p>
            )}
          </div>
        </form>
      </div>
    </div>
  )
}
